import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Modal,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import PlanInformationGiftIcon from "assets/icons/plan-information-gift-icon.svg";
import PlanInformationModalCloseIcon from "assets/icons/plan-information-modal-close.svg";
import PlanInformationShareReferralIcon from "assets/icons/plan-information-share-referral-icon.svg";
import {
  selectSpouseInformation,
  selectUserPersonalInformation
} from "store/reducers/user/UserAccountSlice";
import {
  GENOMIC_LIFE_REFERRAL_INVITE_LINK_LABEL,
  GENOMIC_LIFE_REFERRAL_INVITE_URL
} from "constants/externalPathConstants";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import { COLORS } from "components/v3/Theme/colors";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import GLButton from "components/v3/sharedComponents/GLButton";

const planCardSx = {
  backgroundColor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  maxWidth: "100%",
  minWidth: 0,
  padding: 2.5,
  width: "100%"
} as const;

const shareModalOptionCardSx = {
  alignItems: "flex-start",
  bgcolor: COLORS.PRIMARY_LIGHTEST,
  border: "2px solid transparent",
  borderRadius: "12px",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: 2,
  justifyContent: "flex-start",
  minWidth: 0,
  padding: "35px 25px",
  textAlign: "left",
  transition: "border-color 0.2s ease",
  width: "100%",
  "& .MuiTypography-root": {
    textAlign: "left",
    width: "100%"
  },
  "&.Mui-focusVisible": {
    borderColor: COLORS.PRIMARY_LIGHTER
  },
  "&:hover": {
    borderColor: COLORS.PRIMARY_LIGHTER
  },
  "& img": {
    mb: "25px"
  }
} as const;

const PlanInformationPage: React.FC = () => {
  const history = useHistory();
  const userInformation = useSelector(selectUserPersonalInformation);
  const spouseAccessCode = useSelector(selectSpouseInformation)
    ?.spouseAccessCode;
  const isMemberActive = userInformation.isMemberActive !== false;
  const [cobraModalOpen, setCobraModalOpen] = useState(false);
  const [shareOptionsModalOpen, setShareOptionsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  PageTitle("Plan information");

  const formattedAnnualPrice = new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency"
  }).format(MEMBERSHIP_ANNUAL_PRICE_USD);

  const goToMembershipPayment = () => {
    history.push(PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_PAYMENT);
  };

  const openCobraModal = () => setCobraModalOpen(true);
  const closeCobraModal = () => setCobraModalOpen(false);

  const confirmExtendMembership = () => {
    setCobraModalOpen(false);
    goToMembershipPayment();
  };

  const openShareOptionsModal = () => setShareOptionsModalOpen(true);
  const closeShareOptionsModal = () => setShareOptionsModalOpen(false);

  const shareReferralToGenomicLife = () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator
        .share({
          text: "Check out Genomic Life",
          title: "Genomic Life",
          url: GENOMIC_LIFE_REFERRAL_INVITE_URL
        })
        .catch((err: Error) => {
          if (err?.name !== "AbortError") {
            window.open(
              GENOMIC_LIFE_REFERRAL_INVITE_URL,
              "_blank",
              "noopener,noreferrer"
            );
          }
        });
    } else {
      window.open(
        GENOMIC_LIFE_REFERRAL_INVITE_URL,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleGiftMembershipOption = () => {
    closeShareOptionsModal();
    history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP);
  };

  const handleReferralOption = () => {
    closeShareOptionsModal();
    shareReferralToGenomicLife();
  };

  const showSpouseInviteCard = Boolean(spouseAccessCode && isMemberActive);

  const activeMemberPlanCardContent = (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2
        }}
      >
        <Typography
          variant="body_strong_black"
          sx={{
            color: COLORS.BLACK,
            fontSize: "1.125rem",
            fontWeight: 700
          }}
        >
          Genomic Life Complete
        </Typography>
      </Box>
      <Typography
        component="p"
        variant="body2"
        sx={{
          color: COLORS.DARK_GRAY_2,
          lineHeight: 1.65,
          margin: 0
        }}
      >
        Your Genomic Life Membership is part of your employee benefits package.
        If you change employers or health plans, you may opt to extend the
        benefit as an individual. Please contact your Human Resources Department
        or Genomic Life for more information.
      </Typography>
    </>
  );

  const inactiveMemberPlanCardContent = (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2
        }}
      >
        <Typography
          variant="body_strong_black"
          sx={{
            color: COLORS.BLACK,
            fontSize: "1.125rem",
            fontWeight: 700
          }}
        >
          Genomic Life Complete
        </Typography>
        <Box
          sx={{
            backgroundColor: COLORS.NOTICE,
            borderRadius: "999px",
            px: 1.75,
            py: 0.5
          }}
        >
          <Typography
            component="span"
            sx={{
              color: COLORS.WHITE,
              fontSize: "0.8125rem",
              fontWeight: 600
            }}
          >
            <Box component="span" sx={{ fontStyle: "normal" }}>
              Expired
            </Box>
            <Box component="span" sx={{ fontStyle: "italic" }}>
              {" "}
              - renewal available
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5
        }}
      >
        <Typography
          component="p"
          variant="body2"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            margin: 0
          }}
        >
          Genomic Life helps identify health risks, supports prevention, and
          provides expert 1-on-1 support for appropriate genetics-driven,
          guideline-recommended care. Continue personalizing your approach to
          healthcare with Genomic Life’s 3-in-1 benefit.
        </Typography>
        <Typography
          component="p"
          variant="body2"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            margin: 0
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            DNA Screenings:
          </Box>{" "}
          By looking at your DNA, it’s possible to uncover your personal risk of
          developing a serious illness—so your doctors can act early and help
          you stay healthy.
        </Typography>
        <Typography
          component="p"
          variant="body2"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            margin: 0
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            Personalized Health Guidance:
          </Box>{" "}
          Our team can help you understand your results and take action. Plus,
          if you hit a roadblock with your healthcare, our team can help you get
          back on track. 
        </Typography>
        <Typography
          component="p"
          variant="body2"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            margin: 0
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            Cancer Navigation:
          </Box>{" "}
          If you are diagnosed with cancer, Genomic Life works alongside your
          doctors to help you fully understand treatment options and overcome
          barriers—so you can feel confident in your next steps. 
        </Typography>
      </Box>

      <Divider sx={{ borderColor: alpha(COLORS.DARK_GRAY, 0.25), my: 2.5 }} />

      <Box
        sx={{
          alignItems: { md: "flex-end", xs: "stretch" },
          display: "flex",
          flexDirection: { md: "row", xs: "column" },
          gap: 2,
          justifyContent: "space-between"
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: COLORS.BLACK, fontWeight: 700, mb: 1 }}
          >
            Continue your Genomic Life membership:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.DARK_GRAY,
              fontSize: "0.875rem",
              mb: 0
            }}
          >
            Your yearly cost
          </Typography>
          <Typography
            variant="body_strong_lg"
            sx={{
              color: COLORS.ACCENT2,
              fontSize: "20px",
              fontWeight: 700
            }}
          >
            {formattedAnnualPrice}
          </Typography>
        </Box>
        <GLButton
          buttonType="secondary-transparent"
          onClick={openCobraModal}
          text="Extend membership"
          sx={{
            alignSelf: { md: "auto", xs: "stretch" },
            boxSizing: "border-box",
            maxWidth: { md: "max-content", xs: "100%" },
            minWidth: 0,
            width: { md: "fit-content", xs: "100%" },
            "& .MuiTypography-root": {
              fontSize: "16px"
            }
          }}
        />
      </Box>
    </>
  );

  return (
    <GradientFramedLayout showHeader={true}>
      <Modal
        open={cobraModalOpen}
        onClose={closeCobraModal}
        closeAfterTransition
        aria-labelledby="cobra-disclaimer-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52),
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)"
            }
          }
        }}
      >
        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "18px",
            boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
            boxSizing: "border-box",
            left: "50%",
            maxHeight: "min(90vh, 720px)",
            maxWidth: "720px",
            outline: 0,
            overflow: "auto",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            p: { xs: 3, sm: 4 }
          }}
        >
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              gap: 1.5,
              mb: 2.5
            }}
          >
            <Box
              aria-hidden
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: "21px",
                lineHeight: 1,
                mt: "4px"
              }}
            >
              🛑
            </Box>
            <Typography
              id="cobra-disclaimer-title"
              variant="body_strong_lg"
              component="h2"
              sx={{}}
            >
              Please note: This is not a COBRA program
            </Typography>
          </Box>

          <Typography
            variant="body_strong"
            sx={{
              mb: 2
            }}
          >
            Before enrolling here, find out if COBRA may be applicable by
            contacting your previous employer or plan beneficiary.
          </Typography>

          <Typography variant="body" sx={{}}>
            Genomic Life™ is not an insurance or medical company. The program
            does not pay or reimburse treatment costs of any kind, nor does it
            provide medical treatment, issue referrals, or write prescriptions.
            Access to some services is dependent on a current or previous cancer
            diagnosis. Genomic Life takes your privacy very seriously. No
            identifiable protected health information is provided to any third
            party without your expressed written consent. For more information
            on our policies,{" "}
            <Box
              component={RouterLink}
              rel="noopener noreferrer"
              target="_blank"
              to={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}
              onClick={closeCobraModal}
              sx={{
                color: COLORS.PRIMARY,
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                "&:hover": {
                  color: COLORS.PRIMARY_DARK
                }
              }}
            >
              click here
            </Box>
            .
          </Typography>

          <Box
            sx={{
              alignItems: { sm: "center", xs: "stretch" },
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              gap: 2,
              justifyContent: "space-between",
              mt: 3
            }}
          >
            <GLButton
              buttonType="primary"
              onClick={confirmExtendMembership}
              text="I understand and agree"
              sx={{
                maxWidth: { sm: "none", xs: "100%" },
                width: { sm: "auto", xs: "100%" }
              }}
            />
            <Button
              type="button"
              onClick={closeCobraModal}
              sx={{
                alignSelf: { sm: "auto", xs: "flex-end" },
                color: COLORS.DARK_GRAY,
                fontSize: "1rem",
                fontWeight: 500,
                minWidth: "auto",
                px: 1,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: COLORS.DARK_GRAY_2,
                  textDecoration: "underline"
                }
              }}
            >
              Nevermind
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={shareOptionsModalOpen}
        onClose={closeShareOptionsModal}
        closeAfterTransition
        aria-describedby="share-options-modal-description"
        aria-labelledby="share-options-modal-title"
        slotProps={{
          backdrop: {
            sx: {
              WebkitBackdropFilter: "blur(12px)",
              backdropFilter: "blur(12px)",
              backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52)
            }
          }
        }}
      >
        <Box
          sx={{
            left: "50%",
            maxWidth: "840px",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)"
          }}
        >
          <Box
            sx={{
              bgcolor: COLORS.WHITE,
              borderRadius: "16px",
              boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
              outline: 0,
              position: "relative",
              p: { sm: 4, xs: 3 },
              pt: { sm: 5, xs: 4 }
            }}
          >
            <IconButton
              aria-label="Close dialog"
              onClick={closeShareOptionsModal}
              sx={{
                bgcolor: COLORS.WHITE,
                border: `1px solid ${COLORS.PRIMARY_LIGHT}`,
                boxShadow: "none",
                position: "absolute",
                right: { sm: -25, xs: 10 },
                top: { sm: -25, xs: 10 },
                "&:hover": {
                  bgcolor: COLORS.LIGHT_GRAY,
                  boxShadow: "none"
                }
              }}
            >
              <Box
                alt=""
                aria-hidden
                component="img"
                src={PlanInformationModalCloseIcon}
                sx={{ display: "block", height: 28, width: 28 }}
              />
            </IconButton>

            <Typography
              component="h4"
              id="share-options-modal-title"
              variant="h4_black"
              sx={{
                mb: 0.75
              }}
            >
              Choose an option
            </Typography>
            <Typography
              id="share-options-modal-description"
              variant="body"
              sx={{ mb: 3 }}
            >
              Please select an option below
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                gap: 3
              }}
            >
              <ButtonBase
                aria-label="Gift a membership. Buy a Genomic Life membership for your loved ones."
                focusRipple
                onClick={handleGiftMembershipOption}
                sx={shareModalOptionCardSx}
              >
                <Box
                  alt=""
                  aria-hidden
                  component="img"
                  src={PlanInformationGiftIcon}
                  sx={{ display: "block", height: 79, width: 80 }}
                />
                <Typography
                  component="h3"
                  variant="body_strong_black"
                  sx={{ mb: -1 }}
                >
                  Gift a membership
                </Typography>
                <Typography variant="body_xs_black">
                  Buy a Genomic Life membership for your loved ones.
                </Typography>
              </ButtonBase>

              <ButtonBase
                aria-label="Tell someone about Genomic Life. Send someone a referral to sign up for Genomic Life."
                focusRipple
                onClick={handleReferralOption}
                sx={shareModalOptionCardSx}
              >
                <Box
                  alt=""
                  aria-hidden
                  component="img"
                  src={PlanInformationShareReferralIcon}
                  sx={{ display: "block", height: 79, width: 80 }}
                />
                <Typography
                  component="h3"
                  variant="body_strong_black"
                  sx={{ mb: -1 }}
                >
                  Tell someone about Genomic Life
                </Typography>
                <Typography variant="body_xs_black">
                  Send someone a referral to sign up for Genomic Life.
                </Typography>
              </ButtonBase>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Box
        sx={{
          alignItems: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 840, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Plan Information
        </Typography>

        <BreadcrumbTrail
          segments={[
            { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
            { label: "Plan Information" }
          ]}
        />

        <Box sx={planCardSx}>
          {isMemberActive
            ? activeMemberPlanCardContent
            : inactiveMemberPlanCardContent}
        </Box>

        {showSpouseInviteCard ? (
          <Box data-testid="spouse-invite-card" sx={planCardSx}>
            <Typography
              variant="body_strong_black"
              sx={{
                color: COLORS.BLACK,
                fontSize: "1.125rem",
                fontWeight: 700,
                mb: 2
              }}
            >
              Invite your spouse
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.65, mb: 2 }}
            >
              Good news! Your spouse has been enrolled as part of your plan.
              Please share this access code with your spouse and have them go to{" "}
              {GENOMIC_LIFE_REFERRAL_INVITE_LINK_LABEL} to get started.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLORS.DARK_GRAY_2, fontWeight: 600, mb: 1 }}
            >
              Your spouse’s personal access code:
            </Typography>
            <Typography
              sx={{
                color: COLORS.ACCENT2,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                mb: 2
              }}
            >
              {spouseAccessCode}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.DARK_GRAY,
                fontStyle: "italic",
                lineHeight: 1.5
              }}
            >
              This code is only intended for your spouse. This code expires once
              its used.
            </Typography>
          </Box>
        ) : null}

        <Box
          data-testid="tell-others-genomic-life-card"
          sx={{
            alignItems: { md: "center", xs: "stretch" },
            backgroundColor: COLORS.BACKGROUND_BLUE,
            borderRadius: "14px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: { md: "row", xs: "column" },
            gap: { md: 3, xs: 2 },
            justifyContent: "space-between",

            p: 3
          }}
        >
          <Box>
            <Typography
              component="h2"
              variant="body_lg1_black"
              sx={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 3,
                margin: 0
              }}
            >
              <Box component="span" aria-hidden sx={{ lineHeight: 1 }}>
                🥰
              </Box>
              Tell others about Genomic Life
            </Typography>
            <Typography component="p" variant="body" sx={{ mt: 2 }}>
              We are so glad you found us! If you&apos;ve had a meaningful
              experience with Genomic Life, we would be honored if you referred
              others to join.
            </Typography>
          </Box>
          <GLButton
            buttonType="secondary-transparent"
            onClick={openShareOptionsModal}
            text="Share Genomic Life"
            sx={{
              alignSelf: "flex-end",
              boxSizing: "border-box",
              flexShrink: 0,
              maxWidth: { md: "max-content", xs: "100%" },
              minWidth: 0,
              width: { md: "fit-content", xs: "100%" },
              "& .MuiTypography-root": {
                fontSize: "16px",
                fontWeight: 700
              }
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "100%",
            minWidth: 0,
            width: "100%"
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              maxWidth: { md: "66%", xs: "100%" },
              minWidth: 0,
              width: { md: "66%", xs: "100%" }
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default PlanInformationPage;
